/**
 * Changing a client after it exists.
 *
 * Every value in client.json can change here, including the two files the build reads. The
 * service validates each edit through the Python CLI and puts the old value back if it is
 * refused, so a mistake costs a message rather than a broken client.
 *
 * The package name is set apart. Play binds an app to its package permanently: a new one is a
 * new listing, not a rename, and the existing app keeps whatever was last uploaded under the old
 * one. So it is confirmed by typing it, the way removing a client is.
 */
import { useState } from 'react';
import { ApiError, api, type Client } from '../api';

export function EditClient({ client, onSaved, onCancel, onToast }: {
  client: Client;
  onSaved: () => void;
  onCancel: () => void;
  onToast: (message: string) => void;
}) {
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [packageName, setPackageName] = useState(client.packageName);
  const [confirmed, setConfirmed] = useState('');

  const renaming = packageName.trim() !== client.packageName;
  const blocked = renaming && confirmed.trim() !== packageName.trim();

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setErrors([]);
    setSaving(true);
    const form = new FormData(event.currentTarget);
    // An untouched file input is still a part, and an empty one means "keep what is there".
    for (const field of ['logo', 'googleServices', 'googleServiceInfo']) {
      const file = form.get(field);
      if (file instanceof File && file.size === 0) form.delete(field);
    }

    try {
      await api.updateClient(client.slug, form);
      onToast(renaming ? `Now building ${packageName.trim()}` : 'Client updated');
      onSaved();
    } catch (error) {
      const failure = error as ApiError;
      setErrors(failure.errors?.length ? failure.errors : [failure.message]);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="form section" onSubmit={submit}>
      {errors.length > 0 && (
        <div className="errors">{errors.map((message) => <span key={message}>{message}</span>)}</div>
      )}

      <div className="field">
        <label htmlFor="edit-appName">App name</label>
        <input id="edit-appName" name="appName" type="text" required defaultValue={client.appName} />
      </div>

      <div className="pair">
        <div className="field">
          <label htmlFor="edit-appDomain">Backend URL</label>
          <input id="edit-appDomain" name="appDomain" type="text" required defaultValue={client.appDomain} />
        </div>
        <div className="field">
          <label htmlFor="edit-appId">App ID</label>
          <input id="edit-appId" name="appId" type="number" min={0} required defaultValue={client.appId} />
        </div>
      </div>

      <div className="pair">
        <div className="field">
          <label htmlFor="edit-versionName">Version name</label>
          <input id="edit-versionName" name="versionName" type="text"
                 placeholder="Leave blank to follow the template"
                 defaultValue={client.versionName ?? ''} />
        </div>
        <div className="field">
          <label htmlFor="edit-lastVersionCode">Last version code on Play</label>
          <input id="edit-lastVersionCode" name="lastVersionCode" type="number" min={0}
                 defaultValue={client.lastVersionCode} />
          <span className="hint">Next build: {client.nextVersionCode}.</span>
        </div>
      </div>

      <div className="pair">
        <div className="field">
          <label htmlFor="edit-aboutMenuTitle">About page menu title</label>
          <input id="edit-aboutMenuTitle" name="aboutMenuTitle" type="text"
                 placeholder="found automatically" defaultValue={client.aboutMenuTitle ?? ''} />
        </div>
        <div className="field">
          <label htmlFor="edit-iconBackground">Icon background</label>
          <input id="edit-iconBackground" name="iconBackground" type="text" placeholder="#FFFFFF"
                 defaultValue={client.iconBackground === '#FFFFFF' ? '' : client.iconBackground} />
          <span className="hint">White if left blank.</span>
        </div>
      </div>

      <div className="field">
        <label htmlFor="edit-logo">Replace the logo</label>
        <input id="edit-logo" name="logo" type="file" accept="image/png" />
        <span className="hint">Empty keeps the current one. Square PNG, 1024 px.</span>
      </div>

      <div className="field">
        <label htmlFor="edit-googleServices">Replace google-services.json</label>
        <input id="edit-googleServices" name="googleServices" type="file" accept="application/json,.json" />
        <span className="hint">Empty keeps the current one. Must list the package name below.</span>
      </div>

      <fieldset className="choice">
        <legend>iOS</legend>
        <div className="option-body">
          <div className="field">
            <label htmlFor="edit-bundleId">Bundle identifier</label>
            <input id="edit-bundleId" name="bundleId" type="text" defaultValue={client.bundleId}
                   placeholder={client.packageName} />
            <span className="hint">
              A separate listing in a separate store, so it need not match the package name.
            </span>
          </div>

          <div className="field">
            <label htmlFor="edit-googleServiceInfo">
              {client.hasIosFirebase ? 'Replace GoogleService-Info.plist' : 'Add GoogleService-Info.plist'}
            </label>
            <input id="edit-googleServiceInfo" name="googleServiceInfo" type="file"
                   accept=".plist,application/xml" />
            <span className="hint">
              {client.hasIosFirebase
                ? 'Empty keeps the current one. Must list the bundle identifier above.'
                : 'iOS builds will not start until this is here, for the bundle identifier above.'}
            </span>
          </div>
        </div>
      </fieldset>

      <fieldset className="choice">
        <legend>Package name</legend>
        <div className="option-body">
          <div className="field">
            <label htmlFor="edit-packageName">Package name</label>
            <input id="edit-packageName" name="packageName" type="text" required
                   value={packageName} onChange={(event) => setPackageName(event.target.value)} />
            <span className="hint">
              Changing this builds a different app.{' '}
              <span className="mono">{client.packageName}</span> keeps whatever was last uploaded.
            </span>
          </div>

          {renaming && (
            <div className="field">
              <label htmlFor="edit-confirm">Type the new package name to confirm</label>
              <input id="edit-confirm" type="text" autoComplete="off" value={confirmed}
                     onChange={(event) => setConfirmed(event.target.value)}
                     placeholder={packageName.trim()} />
            </div>
          )}
        </div>
      </fieldset>

      <div className="actions">
        <button className="btn" type="submit" disabled={saving || blocked}>
          {saving ? 'Saving' : 'Save changes'}
        </button>
        <button className="btn quiet" type="button" onClick={onCancel}>Cancel</button>
        <span className="hint">
          The slug <span className="mono">{client.slug}</span> cannot change.
        </span>
      </div>
    </form>
  );
}
